import React, { useState } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  Calendar,
  User,
  ChevronRight,
  Eye,
  Plus
} from 'lucide-react';
import { useHistory } from 'react-router-dom';
import styles from './clientPastMonthCloses.module.css';

// Mock historical data for the current client (Acme Corp)
const users = [
  { id: 1, name: "Sarah Wilson" },
  { id: 2, name: "David Lopez" }
];

const clientHistoricalCloses = [
  {
    id: 1,
    period: "June",
    year: 2025,
    status: "In Progress",
    completedDate: "",
    dueDate: "2025-07-15",
    assignedUserId: 1,
    totalTasks: 21,
    completedTasks: 3,
    daysToComplete: 13,
    isLate: false
  },
  {
    id: 2,
    period: "May",
    year: 2025,
    status: "Completed",
    completedDate: "2025-06-14",
    dueDate: "2025-06-15",
    assignedUserId: 1,
    totalTasks: 21,
    completedTasks: 21,
    daysToComplete: 9,
    isLate: false
  },
  {
    id: 3,
    period: "April",
    year: 2025,
    status: "Completed",
    completedDate: "2025-05-10",
    dueDate: "2025-05-15",
    assignedUserId: 1,
    totalTasks: 20,
    completedTasks: 20,
    daysToComplete: 7,
    isLate: false
  },
  {
    id: 4,
    period: "March",
    year: 2025,
    status: "Completed",
    completedDate: "2025-04-18",
    dueDate: "2025-04-15",
    assignedUserId: 2,
    totalTasks: 19,
    completedTasks: 19,
    daysToComplete: 12,
    isLate: true
  },
  {
    id: 5,
    period: "February",
    year: 2025,
    status: "Completed",
    completedDate: "2025-03-12",
    dueDate: "2025-03-15",
    assignedUserId: 1,
    totalTasks: 21,
    completedTasks: 21,
    daysToComplete: 8,
    isLate: false
  },
  {
    id: 6,
    period: "January",
    year: 2025,
    status: "Incomplete",
    completedDate: null,
    dueDate: "2025-02-15",
    assignedUserId: 2,
    totalTasks: 20,
    completedTasks: 17,
    daysToComplete: null,
    isLate: true
  }
];

export function ClientPastMonthCloses() {
  const [selectedCloseId, setSelectedCloseId] = useState(null);
  const history = useHistory();

  const handleCreateNew = () => {
    history.push('/client/work/close/create');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  const getStatusIcon = (status, isLate) => {
    if (status === 'Completed') {
      return <CheckCircle2 size={16} className={isLate ? styles.statusIconLate : styles.statusIconOnTime} />;
    } else {
      return <AlertTriangle size={16} className={styles.statusIconIncomplete} />;
    }
  };

  const getStatusBadge = (status, isLate) => {
    if (status === 'Completed') {
      return (
        <span className={`${styles.statusBadge} ${isLate ? styles.statusBadgeLate : styles.statusBadgeOnTime}`}>
          {isLate ? 'Late' : 'On Time'}
        </span>
      );
    } else {
      return (
        <span className={`${styles.statusBadge} ${styles.statusBadgeIncomplete}`}>
          Incomplete
        </span>
      );
    }
  };

  // Calculate quick stats
  const completedCloses = clientHistoricalCloses.filter(close => close.status === 'Completed').length;
  const onTimeCloses = clientHistoricalCloses.filter(close => close.status === 'Completed' && !close.isLate).length;

  // Helper to get user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown';
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h3 className={styles.headerTitle}>Accounting Closes</h3>
        </div>
        <div className={styles.stats}>
          <div className={styles.statBlock}>
            <div className={styles.statValue}>{completedCloses}/{clientHistoricalCloses.length}</div>
            <div className={styles.statLabel}>Completed</div>
          </div>
          <div className={styles.statBlock}>
            <div className={`${styles.statValue} ${styles.statValueGreen}`}>{onTimeCloses}/{completedCloses}</div>
            <div className={styles.statLabel}>On Time</div>
          </div>
          <button
            className={styles.newButton}
            onClick={handleCreateNew}
            style={{ marginLeft: 16, display: 'flex', alignItems: 'center', gap: 6, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 500, cursor: 'pointer' }}
          >
            <Plus size={16} style={{ marginRight: 4 }} />
            New Close
          </button>
        </div>
      </div>

      {/* Closes List */}
      <div className={styles.closesList}>
        {clientHistoricalCloses.map(close => (
          <div
            key={close.id}
            className={styles.closeItem}
            onClick={() => setSelectedCloseId(selectedCloseId === close.id ? null : close.id)}
          >
            <div className={styles.closeMain}>
              <div className={styles.closeInfo}>
                <div>
                  {getStatusIcon(close.status, close.isLate)}
                </div>
                <div className={styles.closeText}>
                  <h4 className={styles.closeTitle}>
                    {close.period} {close.year}
                  </h4>
                  <div className={styles.closeMeta}>
                    <div className={styles.closeMetaIcon}>
                      <User size={12} className={styles.closeMetaIcon} />
                      {getUserName(close.assignedUserId)}
                    </div>
                    <div className={styles.closeMetaIcon}>
                      <Calendar size={12} className={styles.closeMetaIcon} />
                      Due: {formatDate(close.dueDate)}
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.closeInfo}>
                <div className={styles.statBlock}>
                  {getStatusBadge(close.status, close.isLate)}
                  <div className={styles.taskProgressText}>
                    {close.completedTasks}/{close.totalTasks} tasks
                  </div>
                </div>
                <div className={styles.taskProgress}>
                  <div className={styles.taskProgressText}>
                    {Math.round((close.completedTasks / close.totalTasks) * 100)}%
                  </div>
                  <div className={styles.progressBar}>
                    <div
                      className={
                        `${styles.progressBarFill} ` +
                        (close.status === 'Completed'
                          ? (close.isLate ? styles.progressBarFillLate : styles.progressBarFillOnTime)
                          : styles.progressBarFillIncomplete)
                      }
                      style={{ width: `${(close.completedTasks / close.totalTasks) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <ChevronRight
                  size={16}
                  className={
                    `${styles.chevron} ${selectedCloseId === close.id ? styles.chevronOpen : ''}`
                  }
                  onClick={e => {
                    e.stopPropagation();
                    history.push(`/client/work/close/${close.id}`);
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>
            {/* Expanded Details */}
            {selectedCloseId === close.id && (
              <div className={styles.details}>
                <div className={styles.detailsGrid}>
                  <div>
                    <span className={styles.detailsLabel}>Due Date:</span>
                    <div className={styles.detailsValue}>{formatDate(close.dueDate)}</div>
                  </div>
                  <div>
                    <span className={styles.detailsLabel}>Completed:</span>
                    <div className={styles.detailsValue}>
                      {close.completedDate ? formatDate(close.completedDate) : 'Not completed'}
                    </div>
                  </div>
                  <div>
                    <span className={styles.detailsLabel}>Days to Complete:</span>
                    <div className={styles.detailsValue}>
                      {close.daysToComplete ? `${close.daysToComplete} days` : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className={styles.detailsLabel}>Status:</span>
                    <div className={
                      close.status === 'Completed'
                        ? (close.isLate ? styles.detailsStatusLate : styles.detailsStatusOnTime)
                        : styles.detailsStatusIncomplete
                    }>
                      {close.status === 'Completed'
                        ? (close.isLate ? 'Completed Late' : 'Completed On Time')
                        : 'Incomplete'
                      }
                    </div>
                  </div>
                </div>
                <div className={styles.detailsButton}>
                  <button
                    className={styles.viewButton}
                    onClick={() => history.push(`/client/work/close/${close.id}`)}
                  >
                    <Eye size={12} style={{ marginRight: 4 }} />
                    View Details
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {clientHistoricalCloses.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <Calendar size={32} />
          </div>
          <h4 className={styles.emptyTitle}>No closes found</h4>
          <p className={styles.emptyText}>This client doesn't have any accounting closes yet.</p>
        </div>
      )}
    </div>
  );
}